/* ============================================
   LITTLE DROP - SHOPIFY THEME JAVASCRIPT
   Premium Baby Boutique
   ============================================ */

(function() {
    'use strict';

    // ============================================
    // GLOBAL UTILITIES
    // ============================================
    const Utils = {
        debounce(func, wait) {
            let timeout;
            return function executedFunction(...args) {
                const later = () => {
                    clearTimeout(timeout);
                    func(...args);
                };
                clearTimeout(timeout);
                timeout = setTimeout(later, wait);
            };
        },

        formatMoney(cents, format = '${{amount}}') {
            if (typeof cents === 'string') {
                cents = cents.replace('.', '');
            }
            
            const value = (cents / 100).toFixed(2);
            return format.replace('{{amount}}', value);
        },

        fetchJSON(url, options = {}) {
            return fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...options.headers
                }
            }).then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            });
        },

        showNotification(message, type = 'success') {
            const notification = document.createElement('div');
            notification.className = `notification notification-${type}`;
            notification.innerHTML = `
                <i class="fas fa-${type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle'}"></i>
                <span>${message}</span>
            `;
            
            const container = document.getElementById('notification-container') || document.body;
            container.appendChild(notification);
            
            setTimeout(() => notification.classList.add('show'), 10);
            setTimeout(() => {
                notification.classList.remove('show');
                setTimeout(() => notification.remove(), 300);
            }, 3000);
        }
    };

    // ============================================
    // CART FUNCTIONALITY
    // ============================================
    const Cart = {
        init() {
            this.drawer = document.getElementById('cart-drawer');
            this.bindEvents();
        },

        bindEvents() {
            // Open cart drawer
            document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openDrawer();
                });
            });

            // Close cart drawer
            document.querySelectorAll('[data-cart-close]').forEach(btn => {
                btn.addEventListener('click', () => this.closeDrawer());
            });

            // Quick add forms
            document.addEventListener('submit', async (e) => {
                if (e.target.matches('.quick-add-form')) {
                    e.preventDefault();
                    await this.addItem(e.target);
                }
            });

            // Keyboard close
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
                    this.closeDrawer();
                }
            });
        },

        openDrawer() {
            if (this.drawer) {
                this.drawer.classList.add('is-open');
                document.body.style.overflow = 'hidden';
            }
        },

        closeDrawer() {
            if (this.drawer) {
                this.drawer.classList.remove('is-open');
                document.body.style.overflow = '';
            }
        },

        async addItem(form) {
            const submitBtn = form.querySelector('button[type="submit"]');
            const originalText = submitBtn.innerHTML;
            
            submitBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
            submitBtn.disabled = true;

            try {
                const formData = new FormData(form);
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    const item = await response.json();
                    Utils.showNotification(`${item.title} added to cart!`, 'success');
                    await this.refreshCart();
                    this.openDrawer();
                } else {
                    throw new Error('Could not add item to cart');
                }
            } catch (error) {
                Utils.showNotification(error.message, 'error');
            } finally {
                submitBtn.innerHTML = originalText;
                submitBtn.disabled = false;
            }
        },

        async updateItem(line, quantity) {
            try {
                const response = await Utils.fetchJSON('/cart/change.js', {
                    method: 'POST',
                    body: JSON.stringify({ line, quantity })
                });

                await this.refreshCart();
                this.updateCartCount(response.item_count);

                if (quantity === 0) {
                    Utils.showNotification('Item removed from cart', 'success');
                }
            } catch (error) {
                Utils.showNotification('Could not update cart', 'error');
            }
        },

        async refreshCart() {
            try {
                const response = await fetch('/?sections=cart-drawer');
                if (response.ok) {
                    const sections = await response.json();
                    if (sections['cart-drawer'] && this.drawer) {
                        const temp = document.createElement('div');
                        temp.innerHTML = sections['cart-drawer'];
                        const newContent = temp.querySelector('#cart-drawer');
                        if (newContent) {
                            this.drawer.innerHTML = newContent.innerHTML;
                        }
                    }
                }
            } catch (error) {
                console.error('Error refreshing cart:', error);
            }
        },

        updateCartCount(count) {
            document.querySelectorAll('.cart-count').forEach(el => {
                el.textContent = `(${count})`;
            });
            document.querySelectorAll('[data-cart-count]').forEach(el => {
                el.textContent = count;
                el.style.display = count > 0 ? '' : 'none';
            });
        }
    };

    // ============================================
    // PRODUCT FUNCTIONALITY
    // ============================================
    const Product = {
        init() {
            this.initGallery();
            this.initVariants();
            this.initQuantity();
            this.initAccordions();
        },

        initGallery() {
            const gallery = document.querySelector('.product-gallery');
            if (!gallery) return;

            const slides = gallery.querySelectorAll('.gallery-slide');
            const thumbs = gallery.querySelectorAll('.gallery-thumb');
            const prevBtn = gallery.querySelector('.gallery-prev');
            const nextBtn = gallery.querySelector('.gallery-next');
            let currentIndex = 0;

            const showSlide = (index) => {
                slides.forEach((slide, i) => slide.classList.toggle('active', i === index));
                thumbs.forEach((thumb, i) => thumb.classList.toggle('active', i === index));
                currentIndex = index;
            };

            thumbs.forEach((thumb, i) => {
                thumb.addEventListener('click', () => showSlide(i));
            });

            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    showSlide((currentIndex - 1 + slides.length) % slides.length);
                });
            }

            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    showSlide((currentIndex + 1) % slides.length);
                });
            }
        },

        initVariants() {
            const swatches = document.querySelectorAll('[data-option-value]');
            const variantSelect = document.getElementById('product-select');

            swatches.forEach(swatch => {
                swatch.addEventListener('click', function() {
                    const optionSwatches = this.closest('.option-swatches');
                    optionSwatches.querySelectorAll('[data-option-value]').forEach(s => {
                        s.classList.remove('active');
                    });
                    this.classList.add('active');

                    // Update selected text
                    const label = this.closest('.product-option').querySelector('.option-selected');
                    if (label) {
                        label.textContent = this.dataset.optionValue;
                    }

                    // Find matching variant
                    if (variantSelect) {
                        const selectedOptions = [];
                        document.querySelectorAll('.product-option').forEach(option => {
                            const active = option.querySelector('[data-option-value].active');
                            if (active) selectedOptions.push(active.dataset.optionValue);
                        });

                        for (const option of variantSelect.options) {
                            if (selectedOptions.every(opt => option.text.includes(opt))) {
                                variantSelect.value = option.value;
                                break;
                            }
                        }
                    }
                });
            });
        },

        initQuantity() {
            document.querySelectorAll('.quantity-selector').forEach(selector => {
                const input = selector.querySelector('.qty-input');
                const decreaseBtn = selector.querySelector('.qty-decrease');
                const increaseBtn = selector.querySelector('.qty-increase');

                if (decreaseBtn) {
                    decreaseBtn.addEventListener('click', () => {
                        input.value = Math.max(1, parseInt(input.value) - 1);
                        input.dispatchEvent(new Event('change'));
                    });
                }

                if (increaseBtn) {
                    increaseBtn.addEventListener('click', () => {
                        input.value = parseInt(input.value) + 1;
                        input.dispatchEvent(new Event('change'));
                    });
                }
            });
        },

        initAccordions() {
            document.querySelectorAll('.accordion-header').forEach(header => {
                header.addEventListener('click', function() {
                    this.closest('.accordion-item').classList.toggle('open');
                });
            });
        }
    };

    // ============================================
    // SEARCH FUNCTIONALITY
    // ============================================
    const Search = {
        init() {
            this.modal = document.getElementById('search-modal');
            this.input = document.getElementById('search-input');
            this.results = document.getElementById('search-results');
            this.bindEvents();
        },

        bindEvents() {
            // Open search
            document.querySelectorAll('[data-search-toggle]').forEach(btn => {
                btn.addEventListener('click', () => this.open());
            });

            // Close search
            document.querySelectorAll('[data-search-close]').forEach(btn => {
                btn.addEventListener('click', () => this.close());
            });

            // Input handler
            if (this.input) {
                this.input.addEventListener('input', Utils.debounce(() => {
                    this.search(this.input.value);
                }, 300));
            }

            // Keyboard
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
                    this.close();
                }
                if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                    e.preventDefault();
                    this.open();
                }
            });
        },

        open() {
            if (this.modal) {
                this.modal.classList.add('active');
                this.input?.focus();
                document.body.style.overflow = 'hidden';
            }
        },

        close() {
            if (this.modal) {
                this.modal.classList.remove('active');
                document.body.style.overflow = '';
                if (this.input) this.input.value = '';
                if (this.results) this.results.innerHTML = '';
            }
        },

        async search(query) {
            if (!query || query.length < 2) {
                if (this.results) this.results.innerHTML = '';
                return;
            }

            try {
                const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
                const data = await response.json();
                this.renderResults(data.resources.results.products);
            } catch (error) {
                console.error('Search error:', error);
            }
        },

        renderResults(products) {
            if (!this.results) return;

            if (!products || products.length === 0) {
                this.results.innerHTML = '<p class="no-results">No products found</p>';
                return;
            }

            this.results.innerHTML = products.map(product => `
                <a href="${product.url}" class="search-result-item">
                    <div class="result-image">
                        ${product.featured_image ? `<img src="${product.featured_image.url}" alt="${product.title}">` : ''}
                    </div>
                    <div class="result-info">
                        <h4>${product.title}</h4>
                        <span class="result-price">${this.formatPrice(product.price)}</span>
                    </div>
                </a>
            `).join('');
        },

        formatPrice(cents) {
            return '$' + (cents / 100).toFixed(2);
        }
    };

    // ============================================
    // MOBILE MENU
    // ============================================
    const MobileMenu = {
        init() {
            this.menu = document.getElementById('mobile-menu');
            this.bindEvents();
        },

        bindEvents() {
            document.querySelectorAll('[data-mobile-menu-toggle]').forEach(btn => {
                btn.addEventListener('click', () => this.toggle());
            });

            document.querySelectorAll('[data-mobile-menu-close]').forEach(btn => {
                btn.addEventListener('click', () => this.close());
            });
        },

        toggle() {
            if (this.menu) {
                this.menu.classList.toggle('is-open');
            }
        },

        close() {
            if (this.menu) {
                this.menu.classList.remove('is-open');
            }
        }
    };

    // ============================================
    // WISHLIST FUNCTIONALITY
    // ============================================
    const Wishlist = {
        storageKey: 'little_drop_wishlist',

        init() {
            this.items = this.getItems();
            this.bindEvents();
            this.updateUI();
        },

        getItems() {
            try {
                return JSON.parse(localStorage.getItem(this.storageKey)) || [];
            } catch {
                return [];
            }
        },

        saveItems() {
            localStorage.setItem(this.storageKey, JSON.stringify(this.items));
        },

        toggle(productId) {
            const index = this.items.indexOf(productId);
            if (index > -1) {
                this.items.splice(index, 1);
                Utils.showNotification('Removed from wishlist', 'success');
            } else {
                this.items.push(productId);
                Utils.showNotification('Added to wishlist!', 'success');
            }
            this.saveItems();
            this.updateUI();
        },

        bindEvents() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('.wishlist-btn');
                if (btn) {
                    e.preventDefault();
                    const productId = btn.dataset.productId;
                    if (productId) this.toggle(productId);
                }
            });
        },

        updateUI() {
            document.querySelectorAll('.wishlist-btn').forEach(btn => {
                const productId = btn.dataset.productId;
                const icon = btn.querySelector('i');
                if (icon && productId) {
                    const isWishlisted = this.items.includes(productId);
                    icon.className = isWishlisted ? 'fas fa-heart' : 'far fa-heart';
                    btn.classList.toggle('active', isWishlisted);
                }
            });
        }
    };

    // ============================================
    // COLLECTION FILTERS
    // ============================================
    const CollectionFilters = {
        init() {
            this.sidebar = document.getElementById('collection-filters');
            this.bindEvents();
        },

        bindEvents() {
            // Filter toggle (mobile)
            const filterToggle = document.getElementById('filter-toggle');
            const filtersClose = document.getElementById('filters-close');

            if (filterToggle) {
                filterToggle.addEventListener('click', () => {
                    this.sidebar?.classList.add('is-open');
                });
            }

            if (filtersClose) {
                filtersClose.addEventListener('click', () => {
                    this.sidebar?.classList.remove('is-open');
                });
            }

            // Filter groups
            document.querySelectorAll('.filter-header').forEach(header => {
                header.addEventListener('click', function() {
                    this.closest('.filter-group').classList.toggle('open');
                });

                // Open by default
                header.closest('.filter-group')?.classList.add('open');
            });

            // Sort select
            const sortSelect = document.getElementById('sort-select');
            if (sortSelect) {
                sortSelect.addEventListener('change', function() {
                    const url = new URL(window.location.href);
                    url.searchParams.set('sort_by', this.value);
                    window.location.href = url.toString();
                });
            }

            // Filter checkboxes
            document.querySelectorAll('.filter-checkbox input').forEach(checkbox => {
                checkbox.addEventListener('change', () => this.applyFilters());
            });
        },

        applyFilters() {
            const url = new URL(window.location.href);
            
            document.querySelectorAll('.filter-checkbox input:checked').forEach(checkbox => {
                url.searchParams.append(checkbox.name, checkbox.value);
            });

            window.location.href = url.toString();
        }
    };

    // ============================================
    // LAZY LOADING
    // ============================================
    const LazyLoad = {
        init() {
            if ('IntersectionObserver' in window) {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            const img = entry.target;
                            if (img.dataset.src) {
                                img.src = img.dataset.src;
                                img.removeAttribute('data-src');
                            }
                            observer.unobserve(img);
                        }
                    });
                });

                document.querySelectorAll('img[data-src]').forEach(img => {
                    observer.observe(img);
                });
            }
        }
    };

    // ============================================
    // ANNOUNCEMENT BAR
    // ============================================
    const AnnouncementBar = {
        init() {
            const bar = document.querySelector('.announcement-bar');
            const closeBtn = bar?.querySelector('.announcement-close');

            if (closeBtn) {
                closeBtn.addEventListener('click', () => {
                    bar.style.display = 'none';
                    sessionStorage.setItem('announcement_closed', 'true');
                });
            }

            if (sessionStorage.getItem('announcement_closed') === 'true' && bar) {
                bar.style.display = 'none';
            }
        }
    };

    // ============================================
    // INTRO LOADER
    // ============================================
    const IntroLoader = {
        init() {
            const loader = document.getElementById('intro-loader');
            if (!loader) return;

            document.body.style.overflow = 'hidden';

            setTimeout(() => {
                loader.classList.add('hidden');
                document.body.style.overflow = '';
            }, 2500);
        }
    };

    // ============================================
    // HEADER SCROLL EFFECT
    // ============================================
    const HeaderScroll = {
        init() {
            const header = document.querySelector('.site-header');
            if (!header) return;

            window.addEventListener('scroll', () => {
                header.classList.toggle('scrolled', window.scrollY > 50);
            }, { passive: true });
        }
    };

    // ============================================
    // SCROLL REVEAL EFFECTS
    // ============================================
    const ScrollEffects = {
        init() {
            const elements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-card');
            if (elements.length === 0) return;

            const check = () => {
                const trigger = window.innerHeight * 0.85;
                elements.forEach(el => {
                    if (el.getBoundingClientRect().top < trigger) {
                        el.classList.add('active');
                    }
                });
            };

            check();
            window.addEventListener('scroll', check, { passive: true });

            // Counter animation
            const counters = document.querySelectorAll('.stat-number');
            if (counters.length > 0) {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            this.animateCounter(entry.target);
                            observer.unobserve(entry.target);
                        }
                    });
                }, { threshold: 0.5 });

                counters.forEach(c => observer.observe(c));
            }
        },

        animateCounter(el) {
            const target = parseInt(el.dataset.target) || 0;
            const duration = 2000;
            const step = target / (duration / 16);
            let current = 0;

            const update = () => {
                current += step;
                if (current < target) {
                    el.textContent = Math.floor(current);
                    requestAnimationFrame(update);
                } else {
                    el.textContent = target;
                }
            };
            update();
        }
    };

    // ============================================
    // BACK TO TOP
    // ============================================
    const BackToTop = {
        init() {
            const btn = document.getElementById('backToTop');
            if (!btn) return;

            window.addEventListener('scroll', () => {
                btn.classList.toggle('visible', window.scrollY > 500);
            }, { passive: true });

            btn.addEventListener('click', () => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            });
        }
    };

    // ============================================
    // SMOOTH SCROLL
    // ============================================
    const SmoothScroll = {
        init() {
            document.querySelectorAll('a[href^="#"]').forEach(anchor => {
                anchor.addEventListener('click', function(e) {
                    const targetId = this.getAttribute('href');
                    if (targetId === '#') return;

                    const target = document.querySelector(targetId);
                    if (target) {
                        e.preventDefault();
                        target.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                });
            });
        }
    };

    // ============================================
    // INITIALIZE
    // ============================================
    document.addEventListener('DOMContentLoaded', () => {
        IntroLoader.init();
        HeaderScroll.init();
        ScrollEffects.init();
        BackToTop.init();
        Cart.init();
        Product.init();
        Search.init();
        MobileMenu.init();
        Wishlist.init();
        CollectionFilters.init();
        LazyLoad.init();
        AnnouncementBar.init();
        SmoothScroll.init();

        console.log('🍼 Little Drop Theme Initialized');
    });

    // Expose utilities globally
    window.LittleDrop = {
        Utils,
        Cart,
        Product,
        Search,
        Wishlist
    };

})();